import type { ColumnSizingState, SortingState } from "@tanstack/react-table";
import { FC, memo, RefObject, useCallback, useEffect, useMemo } from "react";

import { EarlyStoppingSummary } from "@tsmono/inspect-common/types";
import type {
  ColumnFilter,
  FilterSpec,
  FilterType,
} from "@tsmono/inspect-components/columnFilter";
import { formatNoDecimal } from "@tsmono/util";

import { MessageBand } from "../../../components/MessageBand";
import { highlightSample, setDocumentTitle } from "../../../state/actions";
import { useSelectedLogDetails } from "../../../state/hooks";
import { useStore } from "../../../state/store";
import { useCurrentLogFile } from "../../routing/currentSelection";
import { useSampleNavigationActions } from "../../routing/sampleNavigation";
import {
  routeFromFullUrl,
  toFullUrl,
  useLogRouteParams,
} from "../../routing/url";
import { ExtendedColumnDef } from "../../shared/data-grid/columnTypes";
import { isSampleOpenInRoute } from "../../shared/sample";
import { SamplesGrid } from "../../shared/samples-grid/SamplesGrid";
import { SampleRow } from "../../shared/samples-grid/types";

import { SampleFooter } from "./SampleFooter";
import styles from "./SampleList.module.css";
import { SampleSelectionBar } from "./SampleSelectionBar";

interface SampleListProps {
  items: SampleRow[];
  columns: ExtendedColumnDef<SampleRow>[];
  columnVisibility?: Record<string, boolean>;
  /** Controlled row sort (persisted via the per-log samples view) —
   *  forwarded to the grid's controlled sorting mode. */
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  /** Controlled column widths — same per-log persistence path. */
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: (sizing: ColumnSizingState) => void;
  earlyStopping?: EarlyStoppingSummary | null;
  totalItemCount: number;
  running: boolean;
  /** Row layout. `true` = list-style multi-line rows; `false` = compact. */
  multiline?: boolean;
  /** Forwarded to the grid's scroll container so the title bar collapses on
   *  scroll (title-view collapse-on-scroll listens via `useScrollDirection`). */
  scrollRef?: RefObject<HTMLDivElement | null>;
  /** Forwarded to the grid's controlled column-filter mode (see
   *  `SamplesGrid`'s `columnFilters` doc). */
  columnFilters?: Record<string, ColumnFilter>;
  /** Forwarded to the grid's controlled column-filter mode. */
  onColumnFilterChange?: (
    columnId: string,
    filterType: FilterType,
    spec: FilterSpec | null
  ) => void;
  /** Forwarded to the grid (hides all funnels). */
  hideColumnFilters?: boolean;
  /** Every sample in the log, when samples can be checked for export (the
   *  grid then carries the checkbox column); shows the selection bar. */
  selectableSamples?: readonly { id: string | number; epoch: number }[];
}

const makeSampleRowId = (id: string | number, epoch: number) =>
  `${id}-${epoch}`.replace(/\s+/g, "_");

export const SampleList: FC<SampleListProps> = memo((props) => {
  const {
    items,
    columns,
    columnVisibility,
    sorting,
    onSortingChange,
    columnSizing,
    onColumnSizingChange,
    earlyStopping,
    totalItemCount,
    running,
    multiline,
    scrollRef,
    columnFilters,
    onColumnFilterChange,
    hideColumnFilters,
    selectableSamples,
  } = props;

  const sampleNavigation = useSampleNavigationActions();
  const { sampleId: routeSampleId, epoch: routeEpoch } = useLogRouteParams();
  const highlightedSample = useStore((state) => state.log.highlightedSample);

  const selectedLogDetails = useSelectedLogDetails();
  const evalSpec = selectedLogDetails?.eval;
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    setDocumentTitle({ evalSpec });
  }, [evalSpec]);

  const handleRowOpen = useCallback(
    (row: SampleRow) => {
      // Re-clicking the sample that's currently open in the detail view
      // would re-run highlightSample + navigate redundantly — skip only that.
      // Keyed off the route (not highlightedSample, which persists after
      // navigating back to the log and would wrongly ignore the re-click).
      if (
        isSampleOpenInRoute(routeSampleId, routeEpoch, row.sampleId, row.epoch)
      ) {
        return;
      }
      sampleNavigation.showSample(row.sampleId, row.epoch);
    },
    [sampleNavigation, routeSampleId, routeEpoch]
  );

  const getRowHref = (row: SampleRow) => {
    const url = sampleNavigation.getSampleUrl(row.sampleId, row.epoch);
    return url ? toFullUrl(routeFromFullUrl(url)) : undefined;
  };

  const getRowId = useCallback(
    (row: SampleRow) => makeSampleRowId(row.sampleId, row.epoch),
    []
  );

  // Keyboard/click selection moves flow to the selection's owner (zustand),
  // which feeds back through selectedRowId — the grid never shadows it.
  const handleRowSelect = useCallback(
    (row: SampleRow) => highlightSample(row.sampleId, row.epoch, row.logFile),
    []
  );

  const logFile = useCurrentLogFile();
  const selectedRowId =
    highlightedSample?.logFile === logFile && highlightedSample
      ? makeSampleRowId(highlightedSample.id, highlightedSample.epoch)
      : undefined;

  const sampleCount = items.length;

  const warnings = useMemo(() => {
    let errorCount = 0;
    let limitCount = 0;
    for (const item of items) {
      if (item.error || item.data?.error) {
        errorCount += 1;
      }
      if (item.limit || item.data?.limit) {
        limitCount += 1;
      }
    }
    const percentError = sampleCount > 0 ? (errorCount / sampleCount) * 100 : 0;
    const percentLimit = sampleCount > 0 ? (limitCount / sampleCount) * 100 : 0;

    const result: { type: string; msg: string }[] = [];
    if (errorCount > 0) {
      result.push({
        type: "info",
        msg: `INFO: ${errorCount} of ${sampleCount} samples (${formatNoDecimal(percentError)}%) had errors and were not scored.`,
      });
    }
    if (limitCount > 0) {
      result.push({
        type: "info",
        msg: `INFO: ${limitCount} of ${sampleCount} samples (${formatNoDecimal(percentLimit)}%) completed due to exceeding a limit.`,
      });
    }
    if (earlyStopping?.early_stops && earlyStopping.early_stops.length > 0) {
      result.push({
        type: "info",
        msg: `Skipped ${earlyStopping.early_stops.length} samples due to early stopping (${earlyStopping.manager}). `,
      });
    }
    return result;
  }, [items, sampleCount, earlyStopping]);

  return (
    <div className={styles.mainLayout}>
      {warnings.map((warning) => (
        <MessageBand
          id={`sample-warning-message-${warning.type}-${warning.msg}`}
          message={warning.msg}
          type={
            warning.type === "error" || warning.type === "warning"
              ? warning.type
              : "info"
          }
          key={`sample-warning-message-${warning.type}-${warning.msg}`}
        />
      ))}
      {selectableSamples && logFile && (
        <SampleSelectionBar logFile={logFile} samples={selectableSamples} />
      )}
      <SamplesGrid
        rowData={items}
        columnDefs={columns}
        columnVisibility={columnVisibility}
        sorting={sorting}
        onSortingChange={onSortingChange}
        columnSizing={columnSizing}
        onColumnSizingChange={onColumnSizingChange}
        multiline={multiline}
        getRowId={getRowId}
        selectedRowId={selectedRowId}
        onRowSelect={handleRowSelect}
        scrollRef={scrollRef}
        onRowOpen={handleRowOpen}
        getRowHref={getRowHref}
        columnFilters={columnFilters}
        onColumnFilterChange={onColumnFilterChange}
        hideColumnFilters={hideColumnFilters}
      />
      <SampleFooter
        sampleCount={sampleCount}
        totalSampleCount={totalItemCount}
        running={running}
      />
    </div>
  );
});

SampleList.displayName = "SampleList";
